// src/utils/exportToExcel.ts
import ExcelJS from "exceljs"
import { Translation } from "types/questionnaire.types"

// Define the types for the cell data in Array of Arrays (AOA) format
type ExcelCellValue = string | number | boolean | Date | null
type ExcelRow = ExcelCellValue[]
type ExcelSheetDataAOA = ExcelRow[] // For a single sheet of AOA data

// Define the options for the export function
export interface ExportToExcelOptions {
  sheetName?: string // For single AOA sheet
  sheetNames?: string[] // For multiple AOA sheets (data is ExcelSheetDataAOA[])
  designLanguage?: string // For filtering out in translations data
}

// --- MAIN EXPORT FUNCTION ---

/**
 * Exports data to an Excel file.
 *
 * This function can handle three types of data:
 * 1. A single table (Array of Arrays - ExcelSheetDataAOA)
 * 2. Multiple tables on separate sheets (Array of ExcelSheetDataAOA)
 * 3. Translations data (Array of TranslationItem objects)
 *
 * @param data - The data to be exported.
 * @param filename - The name of the file to be created (without extension).
 * @param options - Additional options for the export.
 * @returns True if export was successful (or attempted), false if a critical error occurred.
 */
export const exportToExcel = async (
  data: ExcelSheetDataAOA | ExcelSheetDataAOA[] | Translation[],
  filename: string,
  options: ExportToExcelOptions = {}
): Promise<boolean> => {
  const workbook = new ExcelJS.Workbook()

  try {
    // Case 1: Multiple tables on separate sheets (data is ExcelSheetDataAOA[])
    if (
      options.sheetNames &&
      Array.isArray(options.sheetNames) &&
      Array.isArray(data) &&
      data.length > 0 &&
      Array.isArray(data[0]) &&
      Array.isArray((data as ExcelSheetDataAOA[])[0]?.[0]) // Heuristic for multi-sheet
    ) {
      const multiSheetData = data as ExcelSheetDataAOA[]

      if (multiSheetData.length > options.sheetNames.length) {
        console.warn(
          `Not enough sheet names provided (${options.sheetNames.length}) for ${multiSheetData.length} tables. Using default names for some sheets.`
        )
      }

      multiSheetData.forEach((tableData, index) => {
        if (
          !Array.isArray(tableData) ||
          (tableData.length > 0 && !Array.isArray(tableData[0]))
        ) {
          console.warn(
            `Data for sheet at index ${index} is not a valid Array of Arrays. Skipping.`
          )
          return
        }

        const sheetNameInput = options.sheetNames![index] || `Sheet${index + 1}`
        try {
          const validSheetName = sheetNameInput
            .replace(/[\\/?*[\]]/g, "")
            .substring(0, 31)
          const worksheet = workbook.addWorksheet(validSheetName)

          // Add rows to worksheet
          tableData.forEach((row) => {
            worksheet.addRow(row)
          })
        } catch (tableError) {
          console.error(`Error processing table at index ${index}:`, tableError)
          const errorMsg =
            tableError instanceof Error ? tableError.message : "Unknown error"
          const errorWs = workbook.addWorksheet(`Error_${index + 1}`)
          errorWs.addRow(["Error processing data"])
          errorWs.addRow([errorMsg])
        }
      })

      if (workbook.worksheets.length === 0 && multiSheetData.length > 0) {
        const defaultWs = workbook.addWorksheet("DefaultSheet")
        defaultWs.addRow(["No valid table data processed for multiple sheets"])
      }
    }
    // Case 2: Single table (ExcelSheetDataAOA)
    else if (
      Array.isArray(data) &&
      (data.length === 0 || (data.length > 0 && Array.isArray(data[0])))
    ) {
      const singleSheetData = data as ExcelSheetDataAOA
      const sheetNameInput = options.sheetName || "Sheet1"
      const validSheetName = sheetNameInput
        .replace(/[\\/?*[\]]/g, "")
        .substring(0, 31)
      const worksheet = workbook.addWorksheet(validSheetName)

      // Add rows to worksheet
      singleSheetData.forEach((row) => {
        worksheet.addRow(row)
      })
    }
    // Case 3: Translations data (Array of TranslationItem objects)
    else if (
      Array.isArray(data) &&
      data.length > 0 &&
      typeof data[0] === "object" &&
      !Array.isArray(data[0]) &&
      "identifier" in data[0] &&
      "MASTER" in data[0]
    ) {
      const translationsData = data as Translation[]
      const languages = new Set<string>()

      translationsData.forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (
            key !== "identifier" &&
            key !== "MASTER" &&
            key !== options.designLanguage
          ) {
            languages.add(key)
          }
        })
      })

      if (languages.size === 0 && translationsData.length > 0) {
        console.warn(
          "No translatable languages found after filtering design language. An empty sheet might be created if 'MASTER' is the only other column besides 'identifier'."
        )
      }

      languages.forEach((language) => {
        const sheetData: ExcelSheetDataAOA = [
          ["Identifier", "Original", language],
        ]
        translationsData.forEach((item) => {
          if (language in item && item[language] !== undefined) {
            sheetData.push([item.identifier, item.MASTER, item[language]!])
          }
        })

        if (sheetData.length > 1) {
          const validLanguageSheetName = language
            .replace(/[\\/?*[\]]/g, "")
            .substring(0, 31)
          const worksheet = workbook.addWorksheet(validLanguageSheetName)

          // Add rows to worksheet
          sheetData.forEach((row) => {
            worksheet.addRow(row)
          })
        }
      })

      if (workbook.worksheets.length === 0 && translationsData.length > 0) {
        const defaultWs = workbook.addWorksheet("NoTranslations")
        defaultWs.addRow(["No translation data to export for selected languages"])
      }
    }
    // Fallback / Error Case for unrecognized data format
    else {
      console.error("Invalid data format for Excel export. Data:", data)
      const errorWs = workbook.addWorksheet("DataFormatError")
      errorWs.addRow(["Invalid data format"])
      errorWs.addRow(["Expected array of arrays, or array of TranslationItem objects."])
    }

    if (workbook.worksheets.length === 0) {
      const defaultWs = workbook.addWorksheet("EmptyData")
      defaultWs.addRow(["No data provided to export"])
    }

    // Generate and download the Excel file
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    return true
  } catch (error) {
    console.error("Error generating Excel file:", error)
    const errorMsg =
      error instanceof Error
        ? error.message
        : "Unknown error during Excel generation"
    try {
      const errorWorkbook = new ExcelJS.Workbook()
      const errorWs = errorWorkbook.addWorksheet("ExportError")
      errorWs.addRow(["Error generating Excel file"])
      errorWs.addRow([errorMsg])

      const errorBuffer = await errorWorkbook.xlsx.writeBuffer()
      const errorBlob = new Blob([errorBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })

      const errorUrl = window.URL.createObjectURL(errorBlob)
      const a = document.createElement("a")
      a.href = errorUrl
      a.download = filename.endsWith(".xlsx")
        ? `${filename.slice(0, -5)}_error.xlsx`
        : `${filename}_error.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(errorUrl)
    } catch (fallbackError) {
      console.error(
        "Failed to create and download error Excel file:",
        fallbackError
      )
    }
    return false
  }
}
